import React, { useEffect } from 'react';
import { ArrowLeft, Shield, TrendingUp, Target, AlertCircle, CheckCircle2, BarChart3, Database } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const VulnerabilityManagementPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="dark-container">
      <Header />
      
      {/* Hero Section */}
      <section className="service-hero">
        <div className="dark-content-container">
          <Link to="/" className="back-link">
            <ArrowLeft size={20} />
            Back to Home
          </Link>
          
          <div className="service-hero-content">
            <div className="service-badge">
              <Shield className="service-badge-icon" />
              <span>Vulnerability Management as a Service</span>
            </div>
            
            <h1 className="service-title">
              From Severity-Based to 
              <span className="service-title-accent"> Risk-Based Vulnerability Management</span>
            </h1>
            
            <p className="service-description">
              Transform your vulnerability management program from overwhelming severity lists to 
              strategic, risk-prioritized remediation powered by advanced threat intelligence and 
              business context analysis.
            </p>
            
            <div className="service-stats">
              <div className="service-stat">
                <span className="service-stat-number">87%</span>
                <span className="service-stat-label">Reduction in Critical Vulnerabilities</span>
              </div>
              <div className="service-stat">
                <span className="service-stat-number">60%</span>
                <span className="service-stat-label">Faster Mean Time to Remediation</span>
              </div>
              <div className="service-stat">
                <span className="service-stat-number">24/7</span>
                <span className="service-stat-label">Continuous Monitoring & Threat Intel</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Methodology Section */}
      <section className="methodology-section">
        <div className="dark-content-container">
          <h2 className="display-large">Our Risk-Based Methodology</h2>
          
          <div className="methodology-grid">
            <div className="methodology-step">
              <div className="step-number">01</div>
              <div className="step-content">
                <h3 className="heading-2">Asset Discovery & Classification</h3>
                <p className="body-medium">
                  Comprehensive asset inventory with business criticality scoring, data classification, 
                  and exposure analysis to establish proper context for vulnerability assessment.
                </p>
                <ul className="step-features">
                  <li>Automated network discovery</li>
                  <li>Business impact classification</li>
                  <li>Crown jewel identification</li>
                  <li>Attack surface mapping</li>
                </ul>
              </div>
            </div>

            <div className="methodology-step">
              <div className="step-number">02</div>
              <div className="step-content">
                <h3 className="heading-2">Threat Intelligence Integration</h3>
                <p className="body-medium">
                  Real-time threat intelligence feeds combined with exploit availability analysis 
                  to prioritize vulnerabilities based on actual threat landscape and attacker behavior.
                </p>
                <ul className="step-features">
                  <li>Live exploit availability tracking</li>
                  <li>Threat actor TTPs analysis</li>
                  <li>Industry-specific threat intel</li>
                  <li>Zero-day vulnerability monitoring</li>
                </ul>
              </div>
            </div>

            <div className="methodology-step">
              <div className="step-number">03</div>
              <div className="step-content">
                <h3 className="heading-2">Risk Scoring & Prioritization</h3>
                <p className="body-medium">
                  Advanced risk calculation combining CVSS scores, threat intelligence, asset criticality, 
                  and environmental factors to create actionable remediation priorities.
                </p>
                <ul className="step-features">
                  <li>Multi-factor risk scoring</li>
                  <li>Business context integration</li>
                  <li>Exploitability assessment</li>
                  <li>Impact vs. effort analysis</li>
                </ul>
              </div>
            </div>

            <div className="methodology-step">
              <div className="step-number">04</div>
              <div className="step-content">
                <h3 className="heading-2">Remediation Planning</h3>
                <p className="body-medium">
                  Strategic remediation roadmaps with clear timelines, resource allocation, 
                  and alternative mitigation strategies for complex vulnerability scenarios.
                </p>
                <ul className="step-features">
                  <li>Phased remediation planning</li>
                  <li>Compensating controls design</li>
                  <li>Resource optimization</li>
                  <li>SLA-based scheduling</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Transformation Section */}
      <section className="transformation-section">
        <div className="dark-content-container">
          <div className="transformation-grid">
            <div className="transformation-before">
              <h3 className="heading-2">Traditional Severity-Based Approach</h3>
              <div className="approach-problems">
                <div className="problem-item">
                  <AlertCircle className="problem-icon" />
                  <span>10,000+ vulnerabilities to manage</span>
                </div>
                <div className="problem-item">
                  <AlertCircle className="problem-icon" />
                  <span>No business context consideration</span>
                </div>
                <div className="problem-item">
                  <AlertCircle className="problem-icon" />
                  <span>Resource overwhelm and burnout</span>
                </div>
                <div className="problem-item">
                  <AlertCircle className="problem-icon" />
                  <span>Reactive patching cycles</span>
                </div>
                <div className="problem-item">
                  <AlertCircle className="problem-icon" />
                  <span>Limited threat intelligence integration</span>
                </div>
              </div>
            </div>

            <div className="transformation-arrow">
              <TrendingUp size={40} className="transform-icon" />
            </div>

            <div className="transformation-after">
              <h3 className="heading-2">Cyber-Wiz Risk-Based Model</h3>
              <div className="approach-benefits">
                <div className="benefit-item">
                  <CheckCircle2 className="benefit-icon" />
                  <span>Focus on top 200 critical risks</span>
                </div>
                <div className="benefit-item">
                  <CheckCircle2 className="benefit-icon" />
                  <span>Business-aligned remediation priorities</span>
                </div>
                <div className="benefit-item">
                  <CheckCircle2 className="benefit-icon" />
                  <span>Strategic resource allocation</span>
                </div>
                <div className="benefit-item">
                  <CheckCircle2 className="benefit-icon" />
                  <span>Proactive threat hunting</span>
                </div>
                <div className="benefit-item">
                  <CheckCircle2 className="benefit-icon" />
                  <span>Real-time threat intelligence</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tools & Technologies */}
      <section className="tools-section">
        <div className="dark-content-container">
          <h2 className="display-large">Enterprise-Grade Tools & Integration</h2>
          
          <div className="tools-grid">
            <div className="tool-category">
              <BarChart3 className="tool-icon" />
              <h3 className="heading-3">Vulnerability Scanners</h3>
              <ul className="tool-list">
                <li>Nessus Professional</li>
                <li>Qualys VMDR</li>
                <li>Rapid7 InsightVM</li>
                <li>OpenVAS Enterprise</li>
              </ul>
            </div>

            <div className="tool-category">
              <Database className="tool-icon" />
              <h3 className="heading-3">Threat Intelligence</h3>
              <ul className="tool-list">
                <li>MITRE ATT&CK Framework</li>
                <li>Recorded Future</li>
                <li>CrowdStrike Threat Intel</li>
                <li>AlienVault OTX</li>
              </ul>
            </div>

            <div className="tool-category">
              <Target className="tool-icon" />
              <h3 className="heading-3">Risk Management</h3>
              <ul className="tool-list">
                <li>Kenna Security (Cisco)</li>
                <li>RiskSense Platform</li>
                <li>Cybersixgill DVE Intelligence</li>
                <li>Custom Risk Scoring Engine</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default VulnerabilityManagementPage;